import { useEffect, useState } from 'react';
import { Button, Dialog, Grid, TableRow, TextField, Typography } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import TaskTypeDropdown from './TaskTypeDropdown';
import SingleDatePicker from './SingleDatePicker';
import { useForm } from 'react-hook-form';
import { TaskCreation } from 'src/models/timesheet';
import { useDispatch } from 'react-redux';
import { AppActions } from 'src/store/AppReducer';
import moment from 'moment';
import TaskStatusDropdown from './TaskStatusDropdown';

interface TaskModalProps {
  open: boolean;
  toggle: () => void;
  type: string;
  taskData?: any;
  onSave: (data) => void;
}

const TaskModal: React.FC<TaskModalProps> = ({ open, toggle, type, taskData, onSave }) => {
  const TextFieldStyle = {
    fontSize: '12px',
    width: '100%'
  };

  let initialValues = {
    taskId: null,
    taskName: null,
    // set task type GENERIC
    taskTypeId: 4,
    startDate: new Date(),
    endDate: null,
    referenceId: null,
    status: ''
  } as TaskCreation;

  const dispatch = useDispatch();
  const [taskValues, setTaskValues] = useState(initialValues as TaskCreation);
  const { register, handleSubmit, reset, setValue, formState: { errors }, clearErrors, setError } = useForm<TaskCreation>();

  //to handle backdrop click
  useEffect(() => {
    setTaskValues(initialValues);
    if(open){
      reset();
      if(type !== 'CREATE'){
        setValue('taskTypeId', taskData?.taskTypeId);
        setValue('startDate', new Date(taskData?.startDate));
        setValue('endDate', taskData?.endDate ? new Date(taskData?.endDate) : null);
        setValue('taskName', taskData?.taskName);
        setValue('referenceId', taskData?.referenceId);
        setValue('status', taskData?.status);

        setTaskValues({
          taskId: taskData?.taskId,
          taskName: taskData?.taskName,
          startDate: taskData?.startDate ? new Date(taskData?.startDate) : null,
          endDate: taskData?.endDate ? new Date(taskData?.endDate) : null,
          taskTypeId: taskData?.taskTypeId,
          referenceId: taskData?.referenceId,
          status: taskData?.status
        })
      }
    }
  }, [open]);


  const onSubmit = (data?: TaskCreation) => {
    dispatch(AppActions.showLoader());
    const payload = {
      taskId: taskData?.taskId,
      endDate: taskValues?.endDate ? moment(taskValues?.endDate).format('yyyy-MM-DD') : null,
      startDate: moment(taskValues?.startDate).format('yyyy-MM-DD'),
      taskName: taskValues?.taskName,
      taskTypeId: taskValues?.taskTypeId,
      referenceId: taskValues?.referenceId,
      status: taskValues?.status
    };   
    onSave(payload);
  };

  return (
    <Dialog
      open={open}
      maxWidth="sm"
      fullWidth
      scroll="paper"
      onClose={toggle}
      className="task-modal"
    >
      <form onSubmit={handleSubmit(onSubmit)}>
        <Grid
          item
          xs={12}
          sx={{ p: 3, pb: 2 }}
          display="flex"
          justifyContent="space-between"
        >
          <Typography
            style={{ fontSize: '14px', fontWeight: '700' }}
            variant="h4"
          >
            {type === 'CREATE' ? 'Create Activity' : 'Edit Activity'}
          </Typography>
          <CloseIcon
            onClick={toggle}
            style={{
              fontSize: '17px',
              cursor: 'pointer'
            }}
          />
        </Grid>
        <Grid item xs={12} sx={{ p: 3, pt: 0 }} gap={1}>
          <Grid item xs={12} pb="1rem">
            <Typography fontWeight="light" variant="h5" className="input-lable">
              Activity Name<span style={{ color: 'red' }}>*</span>
            </Typography>
            <TextField
              sx={{ mt: '4px' }}
              {...register('taskName', { required: 'Activity name is required' })}
              className="input-text"
              size="small"
              variant="outlined"
              style={TextFieldStyle}
              placeholder="Enter Activity Name"
              inputProps={{
                max: 100,
                min: 0,
                style: {
                  fontSize: '12px',
                  padding: '6.5px 14px'
                }
              }}
              type="text"
              value={taskValues?.taskName}
              onChange={(e) => {
                setTaskValues((prev) => ({
                  ...prev,
                  taskName: e.target.value
                }));
                setValue('taskName', e.target.value);
                clearErrors(['taskName']);
              }}
            />
            <Typography
              style={{ paddingLeft: 15, paddingRight: 15, fontSize: '11px' }}
              color="red"
            >
              {errors.taskName && errors.taskName.message}
            </Typography>
          </Grid>
          <Grid item xs={12} pb="1rem">
            <TaskTypeDropdown
              {...register('taskTypeId')}
              value={taskValues?.taskTypeId}
              onChange={(e: any) => {
                setTaskValues((prev) => ({
                  ...prev,
                  taskTypeId: e.target.value
                }));
                setValue('taskTypeId', e.target.value);
              }}
            />
            <Typography
              style={{ paddingLeft: 15, paddingRight: 15, fontSize: '11px' }}
              color="red"
            >
              {errors.taskTypeId && errors.taskTypeId.message}
            </Typography>
          </Grid>
          <Grid item xs={12} pb="1rem">
            <TaskStatusDropdown
              {...register('status', { required: 'Activity status is required' })}
              value={taskValues?.status}
              onChange={(e: any) => {
                setTaskValues((prev) => ({
                  ...prev,
                  status: e.target.value
                }));
                setValue('status', e.target.value);
                clearErrors(['status']);
              }}
            />
            <Typography
              style={{ paddingLeft: 15, paddingRight: 15, fontSize: '11px' }}
              color="red"
            >
              {errors.status && errors.status.message}
            </Typography>
          </Grid>
          <Grid item xs={12} pb="1rem">
            <SingleDatePicker
              {...register('startDate')}
              title="Start Date"
              fullWidth={true}
              value={taskValues?.startDate || null}
              onChange={(e: any) => {
                setTaskValues((prev) => ({
                  ...prev,
                  startDate: e
                }));
                setValue('startDate', e);
              }}
            />
          </Grid>
          <Grid item xs={12} pb="1rem">
            <SingleDatePicker
              {...register('endDate')}
              fullWidth={true}
              title="End Date"
              minDate={new Date(taskValues?.startDate)}
              value={taskValues?.endDate || null}
              onChange={(e: any) => {
                setTaskValues((prev) => ({
                  ...prev,
                  endDate: e
                }));
                setValue('endDate', e);
              }}
            />
          </Grid>
          <Grid item xs={12} pb="1rem">
            <Typography fontWeight="light" variant="h5" className="input-lable">
              Reference Id
            </Typography>
            <TextField
              sx={{ mt: '4px' }}
              {...register('referenceId')}
              className="input-text"
              size="small"
              variant="outlined"
              style={TextFieldStyle}
              placeholder="Enter Reference Id"
              inputProps={{
                style: {
                  fontSize: '12px',
                  padding: '6.5px 14px'
                }
              }}
              type="text"
              value={taskValues?.referenceId}
              onChange={(e) => {
                setTaskValues((prev) => ({
                  ...prev,
                  referenceId: e.target.value
                }));
                setValue('referenceId', e.target.value);
                clearErrors(['referenceId']);
              }}
            />
          </Grid>
          <Grid
            item
            sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}
            xs={12}
          >
            <Button
              id="btn-onClose"
              sx={{
                width: '10%',
                fontWeight: 'normal',
                background: '#F5F7FF',
                fontSize: '13px'
              }}
              onClick={() => {
                reset();
                toggle();
              }}
            >
              Cancel
            </Button>
            <Button
              id="btn-onSubmit"
              sx={{
                ml: 2,
                width: '10%',
                fontSize: '13px',
                fontWeight: 'normal',
                background: '#0069E4',
                textTransform: 'capitalize'
              }}
              variant="contained"
              type="submit"
            >
              {type === 'CREATE' ? 'Add' : 'Update'}
            </Button>
          </Grid>
        </Grid>
      </form>
    </Dialog>
  );
};

export default TaskModal;
